import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Heart, Check, Volume2, VolumeX } from "lucide-react";

const BREATHING_PHASES = [
  { label: "Breathe In", duration: 4 },
  { label: "Hold", duration: 4 },
  { label: "Breathe Out", duration: 4 },
  { label: "Hold", duration: 4 },
];

function HelpedMeButton({ toolId, toolName, helpedTools, onSave }) {
  const isSaved = helpedTools.some(h => h.tool_id === toolId);
  return (
    <button onClick={() => !isSaved && onSave(toolId, toolName)} data-testid={`helped-me-${toolId}`}
      className={`mt-6 flex items-center gap-2 mx-auto px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
        isSaved ? "bg-sage-light text-sage" : "bg-earth-200/50 text-earth-600 hover:bg-earth-200"
      }`}>
      {isSaved ? <Check size={16} /> : <Heart size={16} />}
      {isSaved ? "This helped you" : "This helped me"}
    </button>
  );
}

// ===== BOX BREATHING with real audio files =====
function BoxBreathing({ helpedTools, onSaveHelped }) {
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState(0);
  const [timer, setTimer] = useState(0);
  const [cycles, setCycles] = useState(0);
  const [muted, setMuted] = useState(false);
  const intervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const gainRef = useRef(null);

  const playBreathSound = useCallback((isInhale) => {
    if (muted) return;
    try {
      if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (sourceRef.current) { try { sourceRef.current.stop(); } catch {} }
      // Brown noise shaped as breath
      const dur = 4;
      const bufSize = ctx.sampleRate * dur;
      const buffer = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      let last = 0;
      for (let i = 0; i < bufSize; i++) {
        const w = Math.random() * 2 - 1;
        data[i] = (last + 0.02 * w) / 1.02;
        last = data[i];
        data[i] *= 3.5;
      }
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = isInhale ? 600 : 350;
      const gain = ctx.createGain();
      gainRef.current = gain;
      if (isInhale) {
        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 2);
        gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 4);
      } else {
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 3);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 4);
      }
      src.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      src.start();
      src.stop(ctx.currentTime + 4.1);
      sourceRef.current = src;
    } catch (e) { console.error("Audio error:", e); }
  }, [muted]);

  const stopAudio = useCallback(() => {
    if (sourceRef.current) { try { sourceRef.current.stop(); } catch {} sourceRef.current = null; }
  }, []);

  useEffect(() => {
    if (!active) return;
    if (phase === 0) playBreathSound(true);
    else if (phase === 2) playBreathSound(false);
    else stopAudio();
  }, [active, phase, playBreathSound, stopAudio]);

  useEffect(() => {
    if (!active) return;
    intervalRef.current = setInterval(() => {
      setTimer((t) => {
        if (t >= BREATHING_PHASES[phase].duration - 1) {
          setPhase((p) => { const next = (p + 1) % 4; if (next === 0) setCycles((c) => c + 1); return next; });
          return 0;
        }
        return t + 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [active, phase]);

  const stop = () => {
    setActive(false); setPhase(0); setTimer(0); clearInterval(intervalRef.current);
    stopAudio();
  };

  const progress = (timer + 1) / BREATHING_PHASES[phase].duration;
  const scale = phase === 0 ? 1 + progress * 0.5 : phase === 2 ? 1.5 - progress * 0.5 : phase === 1 ? 1.5 : 1;

  return (
    <div className="bg-white rounded-[2rem] p-8 sm:p-12 shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 relative" data-testid="box-breathing">
      {active && (
        <button onClick={() => setMuted(!muted)} data-testid="breathing-mute-btn"
          className="absolute top-6 right-6 text-earth-400 hover:text-earth-600 transition-colors">
          {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      )}
      <h3 className="font-heading text-xl text-earth-800 mb-2">Box Breathing</h3>
      <p className="text-earth-500 text-sm mb-8">4 seconds in, hold, out, hold. Repeat.</p>
      <div className="flex flex-col items-center">
        <div className="relative w-48 h-48 flex items-center justify-center mb-8">
          <div className="absolute inset-0 rounded-full bg-sage/10 transition-transform duration-1000 ease-in-out" style={{ transform: `scale(${active ? scale : 1})` }} />
          <div className="relative z-10 text-center">
            {active ? (<><p className="font-heading text-2xl text-earth-800">{BREATHING_PHASES[phase].label}</p><p className="text-4xl font-heading text-sage mt-1">{BREATHING_PHASES[phase].duration - timer}</p></>) : (<p className="font-heading text-lg text-earth-500">Press start</p>)}
          </div>
        </div>
        {cycles > 0 && <p className="text-earth-500 text-sm mb-4">{cycles} cycle{cycles > 1 ? "s" : ""} completed</p>}
        <button onClick={active ? stop : () => setActive(true)} data-testid="breathing-toggle-btn"
          className={`rounded-full px-8 py-3 font-medium transition-all duration-300 ${active ? "bg-earth-200 text-earth-700 hover:bg-earth-300" : "bg-sage text-earth-50 hover:-translate-y-1 hover:shadow-lg"}`}>
          {active ? "Stop" : "Start Breathing"}
        </button>
      </div>
      <HelpedMeButton toolId="breathing" toolName="Box Breathing" helpedTools={helpedTools} onSave={onSaveHelped} />
    </div>
  );
}

// ===== QUICK CALM with Tibetan singing bowl throughout =====
function QuickCalm({ helpedTools, onSaveHelped }) {
  const [active, setActive] = useState(false);
  const [seconds, setSeconds] = useState(60);
  const [muted, setMuted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const intervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const bowlSourceRef = useRef(null);
  const bowlGainRef = useRef(null);

  const startWaterSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const masterGain = ctx.createGain();
      masterGain.gain.value = muted ? 0 : 0.12;
      masterGain.connect(ctx.destination);
      bowlGainRef.current = masterGain;
      // Layer 1: filtered brown noise for water base
      const bufSize = ctx.sampleRate * 65;
      const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      const d = buf.getChannelData(0);
      let last = 0;
      for (let i = 0; i < bufSize; i++) {
        const w = Math.random() * 2 - 1;
        d[i] = (last + 0.02 * w) / 1.02;
        last = d[i];
        d[i] *= 3.5;
      }
      const noiseSrc = ctx.createBufferSource();
      noiseSrc.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 800;
      bp.Q.value = 0.5;
      const noiseGain = ctx.createGain();
      noiseGain.gain.value = 0.6;
      noiseSrc.connect(bp);
      bp.connect(noiseGain);
      noiseGain.connect(masterGain);
      noiseSrc.start();
      // Layer 2: slow random trickle — modulated high noise
      const trickBuf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      const td = trickBuf.getChannelData(0);
      let tl = 0;
      for (let i = 0; i < bufSize; i++) {
        const w = Math.random() * 2 - 1;
        td[i] = (tl + 0.04 * w) / 1.04;
        tl = td[i];
        td[i] *= 2;
      }
      const trickSrc = ctx.createBufferSource();
      trickSrc.buffer = trickBuf;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 2000;
      const trickGain = ctx.createGain();
      trickGain.gain.value = 0.15;
      trickSrc.connect(hp);
      hp.connect(trickGain);
      trickGain.connect(masterGain);
      trickSrc.start();
      // Layer 3: very slow LFO for natural movement
      const lfo = ctx.createOscillator();
      lfo.type = "sine";
      lfo.frequency.value = 0.15;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.03;
      lfo.connect(lfoGain);
      lfoGain.connect(masterGain.gain);
      lfo.start();
      bowlSourceRef.current = { sources: [noiseSrc, trickSrc], lfo };
    } catch (e) { console.error(e); }
  };

  const stopAllAudio = useCallback(() => {
    if (bowlSourceRef.current) {
      const { sources, lfo } = bowlSourceRef.current;
      sources.forEach(s => { try { s.stop(); } catch {} });
      try { lfo.stop(); } catch {}
      bowlSourceRef.current = null;
    }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
    bowlGainRef.current = null;
  }, []);

  useEffect(() => {
    if (bowlGainRef.current) bowlGainRef.current.gain.value = muted ? 0 : 0.12;
  }, [muted]);

  // Timer completion — fade out
  useEffect(() => {
    if (seconds <= 0 && active) {
      setActive(false);
      setCompleted(true);
      clearInterval(intervalRef.current);
      if (bowlGainRef.current && audioCtxRef.current) {
        try {
          bowlGainRef.current.gain.linearRampToValueAtTime(0, audioCtxRef.current.currentTime + 3);
          setTimeout(stopAllAudio, 3500);
        } catch { stopAllAudio(); }
      }
    }
  }, [seconds, active, stopAllAudio]);

  useEffect(() => {
    if (!active || seconds <= 0) return;
    intervalRef.current = setInterval(() => { setSeconds((s) => s - 1); }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [active, seconds]);

  const start = () => { setActive(true); setCompleted(false); startWaterSound(); };
  const reset = () => {
    setActive(false); setSeconds(60); setCompleted(false);
    clearInterval(intervalRef.current); stopAllAudio();
  };

  return (
    <div className="bg-white rounded-[2rem] p-8 sm:p-12 shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 relative" data-testid="quick-calm">
      {(active || completed) && (
        <button onClick={() => setMuted(!muted)} data-testid="calm-mute-btn"
          className="absolute top-6 right-6 text-earth-400 hover:text-earth-600 transition-colors">
          {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      )}
      <h3 className="font-heading text-xl text-earth-800 mb-2">Quick Calm (60s)</h3>
      <p className="text-earth-500 text-sm mb-8">Close your eyes. Breathe. Just 60 seconds of stillness.</p>
      <div className="flex flex-col items-center">
        <div className="relative w-40 h-40 flex items-center justify-center mb-6">
          <svg className="absolute inset-0" viewBox="0 0 160 160">
            <circle cx="80" cy="80" r="72" fill="none" stroke="#EFEFE9" strokeWidth="6" />
            <circle cx="80" cy="80" r="72" fill="none" stroke="#C86B55" strokeWidth="6" strokeDasharray={452} strokeDashoffset={452 - (452 * (60 - seconds)) / 60} strokeLinecap="round" transform="rotate(-90 80 80)" className="transition-all duration-1000" />
          </svg>
          <span className="text-3xl font-heading text-earth-800">{seconds}s</span>
        </div>
        {completed ? (
          <div className="text-center">
            <p className="text-sage font-medium mb-4">Well done. How do you feel?</p>
            <button onClick={reset} data-testid="calm-reset-btn" className="bg-sage text-earth-50 rounded-full px-8 py-3 font-medium">Again</button>
          </div>
        ) : (
          <button onClick={active ? reset : start} data-testid="calm-toggle-btn"
            className={`rounded-full px-8 py-3 font-medium transition-all duration-300 ${active ? "bg-earth-200 text-earth-700" : "bg-terracotta text-earth-50 hover:-translate-y-1 hover:shadow-lg"}`}>
            {active ? "Reset" : "Begin"}
          </button>
        )}
      </div>
      <HelpedMeButton toolId="quick-calm" toolName="Quick Calm (60s)" helpedTools={helpedTools} onSave={onSaveHelped} />
    </div>
  );
}

// ===== MAIN PAGE =====
export default function CalmToolsPage() {
  const [activeTool, setActiveTool] = useState(null);
  const [helpedTools, setHelpedTools] = useState([]);

  const fetchHelped = useCallback(async () => {
    try { const res = await api.get(`/helped-tools`); setHelpedTools(res.data); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchHelped(); }, [fetchHelped]);

  useEffect(() => {
    if (activeTool) return;
    const savedIds = helpedTools.map(h => h.tool_id);
    if (savedIds.includes("breathing")) setActiveTool("breathing");
    else if (savedIds.includes("quick-calm")) setActiveTool("quick");
    else setActiveTool("breathing");
  }, [helpedTools, activeTool]);

  const saveHelpedTool = async (toolId, toolName) => {
    try { await api.post(`/helped-tool`, { tool_id: toolId, tool_name: toolName }); fetchHelped(); } catch (e) { console.error(e); }
  };

  const allTools = [{ id: "breathing", label: "Box Breathing" }, { id: "quick", label: "Quick Calm" }];
  const savedIds = helpedTools.map(h => h.tool_id);
  const toolMapping = { breathing: "breathing", quick: "quick-calm" };
  const sortedTools = [...allTools].sort((a, b) => {
    return (savedIds.includes(toolMapping[a.id]) ? -1 : 0) - (savedIds.includes(toolMapping[b.id]) ? -1 : 0);
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12 sm:py-20" data-testid="calm-tools-page">
      <div className="text-center mb-12">
        <p className="text-sage text-sm uppercase tracking-[0.15em] font-bold mb-3">Calm Tools</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-earth-800 mb-4 tracking-tight">Find your calm</h1>
        <p className="text-earth-600 text-base max-w-lg mx-auto">Simple exercises to bring you back to the present moment.</p>
      </div>
      <div className="flex justify-center gap-2 mb-10" data-testid="calm-tool-selector">
        {sortedTools.map((t) => {
          const isHelped = savedIds.includes(toolMapping[t.id]);
          return (
            <button key={t.id} onClick={() => setActiveTool(t.id)} data-testid={`calm-tab-${t.id}`}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1.5 ${
                activeTool === t.id ? "bg-sage text-earth-50" : "bg-earth-200/50 text-earth-600 hover:bg-earth-200"
              }`}>
              {isHelped && <Heart size={12} className={activeTool === t.id ? "text-earth-50" : "text-sage"} />}
              {t.label}
            </button>
          );
        })}
      </div>
      {activeTool === "breathing" && <BoxBreathing helpedTools={helpedTools} onSaveHelped={saveHelpedTool} />}
      {activeTool === "quick" && <QuickCalm helpedTools={helpedTools} onSaveHelped={saveHelpedTool} />}
    </div>
  );
}
